import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Globe, Bell, Bot, Server, Plus, Trash2, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useSettings, useUpdateSettings } from '@/hooks/use-settings';
import { useSettingsContext } from '@/contexts/settings-context';
import { getApiClient } from '@/lib/api-client';

export const Route = createFileRoute('/settings/')({
  component: SettingsPage,
});

type McpServerFormState = {
  id: string;
  name: string;
  cmd: string;
  args: string; // Comma-separated string for input, converted to array on save
  enabled: boolean;
  description: string;
  defaultTool?: string;
};

type McpServerErrors = Record<
  string,
  Partial<Record<'name' | 'cmd' | 'args', string>>
>;

const generateMcpServerId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (error) {
    // Ignore and fallback to manual id
  }
  return `mcp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const formatSyncedAt = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general');
  
  // API hooks
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  
  // Settings context for header save button
  const { setSaveHandler, setIsSaving, setSaveMessage, setSaveError } = useSettingsContext();

  // Model Settings state - organized by provider
  const [chatModelProvider, setChatModelProvider] = useState('custom-openai');
  
  // Provider-specific states
  const [customOpenAIApiKey, setCustomOpenAIApiKey] = useState('');
  const [customOpenAIBaseUrl, setCustomOpenAIBaseUrl] = useState('');
  const [customOpenAIModelName, setCustomOpenAIModelName] = useState('');
  
  const [openAIApiKey, setOpenAIApiKey] = useState('');
  const [openAIModelName, setOpenAIModelName] = useState('gpt-4o');
  
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  // Default to Haiku 3 (nhẹ, rate limit khá cao). Người dùng có thể nhập tay model 4.x mới.
  const [anthropicModelName, setAnthropicModelName] = useState('claude-3-haiku-20240307');
  
  const [googleApiKey, setGoogleApiKey] = useState('');
  const [googleProjectId, setGoogleProjectId] = useState('');
  const [googleModelName, setGoogleModelName] = useState('gemini-pro');
  
  const [cohereApiKey, setCohereApiKey] = useState('');
  const [cohereModelName, setCohereModelName] = useState('command');
  
  const [embeddingModelProvider, setEmbeddingModelProvider] = useState('hugging-face');
  const [embeddingModel, setEmbeddingModel] = useState('bge-small');

  const [mcpServers, setMcpServers] = useState<McpServerFormState[]>([]);
  const [mcpErrors, setMcpErrors] = useState<McpServerErrors>({});
  const [mcpFormError, setMcpFormError] = useState<string | null>(null);
  const [mcpServerTools, setMcpServerTools] = useState<Record<string, { tools: Array<{ name: string; description?: string }>; loading: boolean; error?: string; lastChecked?: Date }>>({});
  const [expandedServerTools, setExpandedServerTools] = useState<Record<string, boolean>>({});

  const validateMcpServers = useCallback((servers: McpServerFormState[]) => {
    const errors: McpServerErrors = {};
    let isValid = true;

    servers.forEach((server) => {
      const serverErrors: Partial<Record<'name' | 'cmd' | 'args', string>> = {};

      if (!server.name.trim()) {
        serverErrors.name = 'Tên máy chủ là bắt buộc';
      }

      if (!server.cmd.trim()) {
        serverErrors.cmd = 'Command là bắt buộc (ví dụ: bun, node, tsx)';
      }

      if (!server.args.trim()) {
        serverErrors.args = 'Arguments là bắt buộc (ví dụ: run apps/mcp-servers/example-server/src/index.ts)';
      }

      if (Object.keys(serverErrors).length > 0) {
        errors[server.id] = serverErrors;
        isValid = false;
      }
    });

    return { isValid, errors };
  }, []);

  const handleAddMcpServer = () => {
    setMcpServers((prev) => [
      ...prev,
      {
        id: generateMcpServerId(),
        name: '',
        cmd: '',
        args: '',
        enabled: true,
        description: '',
        defaultTool: '',
      },
    ]);
    setMcpErrors({});
    setMcpFormError(null);
  };

  const handleRemoveMcpServer = (id: string) => {
    setMcpServers((prev) => prev.filter((server) => server.id !== id));
    setMcpErrors((prev) => {
      if (!prev[id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setMcpServerTools((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setMcpFormError(null);
  };

  const fetchMcpTools = useCallback(async (servers: Array<{ name: string; enabled?: boolean }>, serverIdMap?: Map<string, string>) => {
    // Gateway chỉ load server đầu tiên được enabled, nên ta fetch tools từ gateway
    // Tools sẽ từ server đầu tiên được enabled
    const enabledServers = servers.filter(s => s.enabled !== false);
    if (enabledServers.length === 0) {
      return;
    }

    try {
      const apiClient = getApiClient();
      
      // Get current mcpServers state to find server IDs
      setMcpServerTools((prev) => {
        const next = { ...prev };
        enabledServers.forEach(server => {
          // Find server ID from map or use name as fallback
          const serverId = serverIdMap?.get(server.name) || server.name;
          next[serverId] = {
            ...next[serverId],
            loading: true,
            error: undefined,
          };
        });
        return next;
      });

      // Fetch tools from gateway (will return tools from first enabled server)
      const response = await apiClient.request<{ success: boolean; tools?: Array<{ name: string; description?: string }>; error?: string }>('/chat/tools');
      
      if (response.success && response.tools) {
        // Assign tools to first enabled server (since gateway only loads first one)
        const firstEnabledServer = enabledServers[0];
        const serverId = serverIdMap?.get(firstEnabledServer.name) || firstEnabledServer.name;
        
        setMcpServerTools((prev) => ({
          ...prev,
          [serverId]: {
            tools: response.tools || [],
            loading: false,
            error: undefined,
            lastChecked: new Date(),
          },
        }));
      } else {
        throw new Error(response.error || 'Failed to fetch tools');
      }
    } catch (error) {
      console.error('Failed to fetch MCP tools:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tools';
      
      // Set error for all enabled servers
      setMcpServerTools((prev) => {
        const next = { ...prev };
        enabledServers.forEach(server => {
          const serverId = serverIdMap?.get(server.name) || server.name;
          next[serverId] = {
            ...next[serverId],
            loading: false,
            error: errorMessage,
          };
        });
        return next;
      });
    }
  }, []);

  const handleMcpFieldChange = (
    id: string,
    field: keyof McpServerFormState,
    value: string | boolean
  ) => {
    setMcpServers((prev) =>
      prev.map((server) => {
        if (server.id !== id) {
          return server;
        }

        if (field === 'enabled') {
          return {
            ...server,
            enabled: Boolean(value),
          };
        }

        if (field === 'name') {
          return {
            ...server,
            name: String(value),
          };
        }

        if (field === 'cmd') {
          return {
            ...server,
            cmd: String(value),
          };
        }

        if (field === 'args') {
          return {
            ...server,
            args: String(value),
          };
        }

        if (field === 'defaultTool') {
          return {
            ...server,
            defaultTool: String(value),
          };
        }

        if (field === 'description') {
          return {
            ...server,
            description: String(value),
          };
        }

        return server;
      })
    );

    if (field === 'name' || field === 'cmd' || field === 'args') {
      setMcpErrors((prev) => {
        if (!prev[id]) {
          return prev;
        }
        const next = { ...prev };
        const { [field]: _removed, ...rest } = next[id] ?? {};
        if (Object.keys(rest).length === 0) {
          delete next[id];
        } else {
          next[id] = rest;
        }
        return next;
      });
    }

    if (mcpFormError) {
      setMcpFormError(null);
    }
  };
  

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'mcp', label: 'MCP Servers', icon: Server },
    { id: 'models', label: 'Model Settings', icon: Bot },
  ];

  const handleSave = useCallback(async () => {
    setSaveMessage(null);
    setSaveError(null);
    
    try {
      // Prepare settings data
      let settingsPayload: { model_settings?: any; mcp_servers?: any[] } = {};
      
      if (activeTab === 'models') {
        // Build provider-specific config
        const providerConfig: Record<string, any> = {
          provider: chatModelProvider,
        };
        
        switch (chatModelProvider) {
          case 'custom-openai':
            providerConfig.apiKey = customOpenAIApiKey;
            providerConfig.baseUrl = customOpenAIBaseUrl;
            providerConfig.modelName = customOpenAIModelName;
            break;
          case 'openai':
            providerConfig.apiKey = openAIApiKey;
            providerConfig.modelName = openAIModelName;
            break;
          case 'anthropic':
            providerConfig.apiKey = anthropicApiKey;
            providerConfig.modelName = anthropicModelName;
            break;
          case 'google':
            providerConfig.apiKey = googleApiKey;
            providerConfig.projectId = googleProjectId;
            providerConfig.modelName = googleModelName;
            break;
          case 'cohere':
            providerConfig.apiKey = cohereApiKey;
            providerConfig.modelName = cohereModelName;
            break;
        }
        
        settingsPayload.model_settings = {
          chatModel: providerConfig,
          embeddingModel: {
            provider: embeddingModelProvider,
            model: embeddingModel,
          },
        };
      } else if (activeTab === 'mcp') {
        const { isValid, errors } = validateMcpServers(mcpServers);

        if (!isValid) {
          const errorMessage = 'Please fix the highlighted MCP server details before saving.';
          setMcpErrors(errors);
          setMcpFormError(errorMessage);
          setSaveError(errorMessage);
          setTimeout(() => {
            setSaveError(null);
          }, 5000);
          return;
        }

        setMcpErrors({});
        setMcpFormError(null);

        const sanitizedServers = mcpServers.map((server) => {
          // Parse args string into array (split by space, but handle quoted strings)
          const argsArray = server.args
            .trim()
            .split(/\s+/)
            .filter(Boolean);

          return {
            name: server.name.trim(),
            cmd: server.cmd.trim(),
            args: argsArray,
            enabled: server.enabled ?? true,
            description: server.description.trim() ? server.description.trim() : undefined,
            defaultTool: server.defaultTool?.trim() ? server.defaultTool.trim() : undefined,
          };
        });

        settingsPayload.mcp_servers = sanitizedServers;
      }
      
      // Call API to save settings
      await updateSettings.mutateAsync(settingsPayload);
      
      // If MCP servers were saved, fetch tools for enabled servers
      if (activeTab === 'mcp' && settingsPayload.mcp_servers) {
        // Create map of server name to ID
        const serverIdMap = new Map<string, string>();
        mcpServers.forEach(server => {
          serverIdMap.set(server.name, server.id);
        });
        await fetchMcpTools(settingsPayload.mcp_servers, serverIdMap);
      }
      
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save settings. Please try again.');
      setTimeout(() => {
        setSaveError(null);
      }, 5000);
    }
  }, [
    activeTab,
    chatModelProvider,
    customOpenAIApiKey,
    customOpenAIBaseUrl,
    customOpenAIModelName,
    openAIApiKey,
    openAIModelName,
    anthropicApiKey,
    anthropicModelName,
    googleApiKey,
    googleProjectId,
    googleModelName,
    cohereApiKey,
    cohereModelName,
    embeddingModelProvider,
    embeddingModel,
    mcpServers,
    validateMcpServers,
    updateSettings,
    setSaveMessage,
    setSaveError,
    setMcpErrors,
    setMcpFormError,
  ]);

  // Register save handler with context for header button
  // setSaveHandler is memoized, so we don't need it in dependencies
  useEffect(() => {
    setSaveHandler(handleSave);
  }, [handleSave]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Update isSaving state when mutation state changes
  useEffect(() => {
    setIsSaving(updateSettings.isPending);
  }, [updateSettings.isPending, setIsSaving]);
  
  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      setSaveHandler(async () => {});
      setIsSaving(false);
      setSaveMessage(null);
      setSaveError(null);
    };
  }, [setSaveHandler, setIsSaving, setSaveMessage, setSaveError]);

  // Load settings from API when they're available
  useEffect(() => {
    if (settings?.model_settings) {
      const modelSettings = settings.model_settings;
      
      // Load chat model settings
      if (modelSettings.chatModel) {
        setChatModelProvider(modelSettings.chatModel.provider || 'custom-openai');
        
        switch (modelSettings.chatModel.provider) {
          case 'custom-openai':
            setCustomOpenAIApiKey(modelSettings.chatModel.apiKey || '');
            setCustomOpenAIBaseUrl(modelSettings.chatModel.baseUrl || '');
            setCustomOpenAIModelName(modelSettings.chatModel.modelName || '');
            break;
          case 'openai':
            setOpenAIApiKey(modelSettings.chatModel.apiKey || '');
            setOpenAIModelName(modelSettings.chatModel.modelName || 'gpt-4o');
            break;
          case 'anthropic':
            setAnthropicApiKey(modelSettings.chatModel.apiKey || '');
            setAnthropicModelName(
              modelSettings.chatModel.modelName || 'claude-3-haiku-20240307'
            );
            break;
          case 'google':
            setGoogleApiKey(modelSettings.chatModel.apiKey || '');
            setGoogleProjectId(modelSettings.chatModel.projectId || '');
            setGoogleModelName(modelSettings.chatModel.modelName || 'gemini-pro');
            break;
          case 'cohere':
            setCohereApiKey(modelSettings.chatModel.apiKey || '');
            setCohereModelName(modelSettings.chatModel.modelName || 'command');
            break;
        }
      }
      
      // Load embedding model settings
      if (modelSettings.embeddingModel) {
        setEmbeddingModelProvider(modelSettings.embeddingModel.provider || 'hugging-face');
        setEmbeddingModel(modelSettings.embeddingModel.model || 'bge-small');
      }
    }

    if (settings) {
      if (settings.mcp_servers && Array.isArray(settings.mcp_servers)) {
        const mappedServers: McpServerFormState[] = settings.mcp_servers.map((server, index) => {
          const normalizedId =
            typeof server.id === 'string'
              ? server.id
              : server.id != null
                ? String(server.id)
                : generateMcpServerId();

          return {
            id: normalizedId,
            name: server.name || '',
            cmd: server.cmd || '',
            args: Array.isArray(server.args) ? server.args.join(' ') : (server.args || ''),
            enabled: server.enabled ?? true,
            description: server.description || '',
            defaultTool: server.defaultTool || '',
          };
        });
        setMcpServers(mappedServers);
      } else {
        setMcpServers([]);
      }
      setMcpErrors({});
      setMcpFormError(null);
    }
  }, [settings]);

  return (
    <div className="space-y-6">
      {/* Settings Navigation */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Settings Content */}
        <div className="p-6 space-y-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>

                <div className="grid gap-6">
                  <div>
                    <label htmlFor="site-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Site Name
                    </label>
                    <Input
                      id="site-name"
                      defaultValue="TrangVang.AI"
                      className="border-gray-200"
                    />
                  </div>

                  <div>
                    <label htmlFor="site-url" className="block text-sm font-medium text-gray-700 mb-2">
                      Site URL
                    </label>
                    <Input
                      id="site-url"
                      defaultValue="https://trangvang.ai"
                      className="border-gray-200"
                    />
                  </div>

                  <div>
                    <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Email
                    </label>
                    <Input
                      id="admin-email"
                      defaultValue="admin@trangvang.ai"
                      type="email"
                      className="border-gray-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">User Settings</h3>

                <div className="grid gap-6">
                  <div>
                    <label htmlFor="default-role" className="block text-sm font-medium text-gray-700 mb-2">
                      Default User Role
                    </label>
                    <Input
                      id="default-role"
                      defaultValue="seeker"
                      className="border-gray-200"
                    />
                  </div>

                  <div>
                    <label htmlFor="max-users" className="block text-sm font-medium text-gray-700 mb-2">
                      Maximum Users
                    </label>
                    <Input
                      id="max-users"
                      defaultValue="1000"
                      type="number"
                      className="border-gray-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>

                <div className="grid gap-6">
                  <div>
                    <label htmlFor="email-notifications" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Notifications
                    </label>
                    <Input
                      id="email-notifications"
                      defaultValue="enabled"
                      className="border-gray-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mcp' && (
            <div className="space-y-6">
              <div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-1">MCP Server Connections</h3>
                    <p className="text-sm text-gray-500 max-w-2xl">
                      Quản lý các máy chủ Model Context Protocol dùng để kết nối TrangVang.AI với hệ thống MCP bên ngoài.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddMcpServer}
                    variant="outline"
                    className="inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Thêm máy chủ
                  </Button>
                </div>

                {mcpFormError && (
                  <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
                    <p className="text-sm font-medium text-red-600">{mcpFormError}</p>
                  </div>
                )}

                <div className="mt-4 space-y-4">
                  {mcpServers.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
                      <p className="text-sm text-gray-500">
                        Chưa có cấu hình máy chủ MCP. Nhấn &quot;Thêm máy chủ&quot; để tạo kết nối mới.
                      </p>
                    </div>
                  ) : (
                    mcpServers.map((server, index) => {
                      const serverErrors = mcpErrors[server.id] || {};
                      return (
                        <div
                          key={server.id}
                          className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                MCP #{index + 1}
                              </span>
                              <h4 className="text-base font-semibold text-gray-900">
                                {server.name || 'Máy chủ MCP mới'}
                              </h4>
                              <p className="text-sm text-gray-500">
                                {server.cmd ? `${server.cmd} ${server.args}` : 'Chưa cấu hình command'}
                              </p>
                            </div>
                            <div className="flex items-center gap-4">
                              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={server.enabled}
                                  onChange={(event) =>
                                    handleMcpFieldChange(server.id, 'enabled', event.target.checked)
                                  }
                                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                Kích hoạt
                              </label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMcpServer(server.id)}
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Xoá
                              </Button>
                            </div>
                          </div>

                          <div className="mt-4 grid gap-6 md:grid-cols-2">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Tên máy chủ <span className="text-red-500">*</span>
                              </label>
                              <Input
                                value={server.name}
                                onChange={(event) =>
                                  handleMcpFieldChange(server.id, 'name', event.target.value)
                                }
                                placeholder="Ví dụ: MCP nội bộ"
                                className={`border-gray-200 ${serverErrors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                              />
                              {serverErrors.name && (
                                <p className="text-sm text-red-500">{serverErrors.name}</p>
                              )}
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Command <span className="text-red-500">*</span>
                              </label>
                              <Input
                                value={server.cmd}
                                onChange={(event) =>
                                  handleMcpFieldChange(server.id, 'cmd', event.target.value)
                                }
                                placeholder="bun, node, tsx, python3"
                                className={`border-gray-200 font-mono text-sm ${serverErrors.cmd ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                              />
                              {serverErrors.cmd && (
                                <p className="text-sm text-red-500">{serverErrors.cmd}</p>
                              )}
                              <p className="text-xs text-gray-500">
                                Command để chạy MCP server (ví dụ: bun, node, tsx)
                              </p>
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Arguments <span className="text-red-500">*</span>
                              </label>
                              <Input
                                value={server.args}
                                onChange={(event) =>
                                  handleMcpFieldChange(server.id, 'args', event.target.value)
                                }
                                placeholder="run apps/mcp-servers/example-server/src/index.ts"
                                className={`border-gray-200 font-mono text-sm ${serverErrors.args ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                              />
                              {serverErrors.args && (
                                <p className="text-sm text-red-500">{serverErrors.args}</p>
                              )}
                              <p className="text-xs text-gray-500">
                                Arguments cho command, phân cách bằng khoảng trắng (ví dụ: run apps/mcp-servers/example-server/src/index.ts)
                              </p>
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                MCP Tool mặc định
                              </label>
                              <Input
                                value={server.defaultTool ?? ''}
                                onChange={(event) =>
                                  handleMcpFieldChange(server.id, 'defaultTool', event.target.value)
                                }
                                placeholder="Ví dụ: perplexica.search"
                                className="border-gray-200 font-mono text-sm"
                              />
                              <p className="text-xs text-gray-500">
                                Để trống để sử dụng tool đầu tiên mà máy chủ MCP cung cấp.
                              </p>
                            </div>

                            <div className="space-y-2 md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Mô tả
                              </label>
                              <Textarea
                                value={server.description}
                                onChange={(event) =>
                                  handleMcpFieldChange(server.id, 'description', event.target.value)
                                }
                                placeholder="Ghi chú về phạm vi hoặc quyền truy cập của máy chủ MCP này"
                                className="border-gray-200"
                                rows={3}
                              />
                            </div>
                          </div>

                          {/* Tools Display */}
                          {server.enabled && (
                            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <h5 className="text-sm font-medium text-gray-900">MCP Tools</h5>
                                  {mcpServerTools[server.id]?.loading && (
                                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                  )}
                                  {mcpServerTools[server.id]?.tools && !mcpServerTools[server.id]?.loading && (
                                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                      {mcpServerTools[server.id].tools.length} tools
                                    </span>
                                  )}
                                  {mcpServerTools[server.id]?.error && !mcpServerTools[server.id]?.loading && (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {mcpServerTools[server.id]?.lastChecked && (
                                    <span className="text-xs text-gray-500">
                                      Kiểm tra: {mcpServerTools[server.id].lastChecked.toLocaleTimeString('vi-VN')}
                                    </span>
                                  )}
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const enabledServers = mcpServers.filter(s => s.enabled !== false);
                                      const sanitizedServers = enabledServers.map((s) => ({
                                        name: s.name.trim(),
                                        cmd: s.cmd.trim(),
                                        args: s.args.trim().split(/\s+/).filter(Boolean),
                                        enabled: s.enabled ?? true,
                                      }));
                                      // Create map of server name to ID
                                      const serverIdMap = new Map<string, string>();
                                      mcpServers.forEach(s => {
                                        serverIdMap.set(s.name, s.id);
                                      });
                                      fetchMcpTools(sanitizedServers, serverIdMap);
                                    }}
                                    disabled={mcpServerTools[server.id]?.loading}
                                    className="h-7 text-xs"
                                  >
                                    {mcpServerTools[server.id]?.loading ? 'Đang tải...' : 'Làm mới'}
                                  </Button>
                                  {mcpServerTools[server.id]?.tools && mcpServerTools[server.id].tools.length > 0 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setExpandedServerTools(prev => ({
                                        ...prev,
                                        [server.id]: !prev[server.id]
                                      }))}
                                      className="h-7 w-7 p-0"
                                    >
                                      {expandedServerTools[server.id] ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>

                              {mcpServerTools[server.id]?.error && (
                                <p className="mt-2 text-sm text-red-600">
                                  {mcpServerTools[server.id].error}
                                </p>
                              )}

                              {mcpServerTools[server.id]?.tools && mcpServerTools[server.id].tools.length > 0 && expandedServerTools[server.id] && (
                                <div className="mt-3 space-y-2">
                                  {mcpServerTools[server.id].tools.map((tool, toolIndex) => (
                                    <div
                                      key={toolIndex}
                                      className="rounded-md border border-gray-200 bg-white p-3"
                                    >
                                      <div className="flex items-start gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 font-mono">
                                            {tool.name}
                                          </p>
                                          {tool.description && (
                                            <p className="mt-1 text-xs text-gray-500">
                                              {tool.description}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {mcpServerTools[server.id]?.tools && mcpServerTools[server.id].tools.length === 0 && !mcpServerTools[server.id]?.loading && (
                                <p className="mt-2 text-sm text-gray-500">
                                  Không có tools nào được tìm thấy từ server này.
                                </p>
                              )}

                              {!mcpServerTools[server.id] && !mcpServerTools[server.id]?.loading && (
                                <p className="mt-2 text-sm text-gray-500">
                                  Chưa kiểm tra tools. Nhấn "Làm mới" để tải danh sách tools.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'models' && (
            <div className="space-y-6">
              {/* Chat Model Settings */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Chat Model Settings</h3>

                <div className="grid gap-6">
                  <div>
                    <label htmlFor="chat-model-provider" className="block text-sm font-medium text-gray-700 mb-2">
                      Chat Model Provider
                    </label>
                    <Select value={chatModelProvider} onValueChange={setChatModelProvider}>
                      <SelectTrigger id="chat-model-provider" className="border-gray-200">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom-openai">Custom OpenAI</SelectItem>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="anthropic">Anthropic</SelectItem>
                        <SelectItem value="google">Google</SelectItem>
                        <SelectItem value="cohere">Cohere</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom OpenAI Fields */}
                  {chatModelProvider === 'custom-openai' && (
                    <>
                      <div>
                        <label htmlFor="custom-openai-api-key" className="block text-sm font-medium text-gray-700 mb-2">
                          Custom OpenAI API Key <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="custom-openai-api-key"
                          type="password"
                          value={customOpenAIApiKey}
                          onChange={(e) => setCustomOpenAIApiKey(e.target.value)}
                          placeholder="Enter your API key"
                          className="border-gray-200 font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Your API key will be encrypted and stored securely
                        </p>
                      </div>

                      <div>
                        <label htmlFor="custom-openai-base-url" className="block text-sm font-medium text-gray-700 mb-2">
                          Custom OpenAI Base URL <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="custom-openai-base-url"
                          value={customOpenAIBaseUrl}
                          onChange={(e) => setCustomOpenAIBaseUrl(e.target.value)}
                          placeholder="https://api.z.ai/api/coding/paas/v4"
                          className="border-gray-200 font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Base URL for the OpenAI-compatible API endpoint
                        </p>
                      </div>

                      <div>
                        <label htmlFor="custom-openai-model-name" className="block text-sm font-medium text-gray-700 mb-2">
                          Model Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="custom-openai-model-name"
                          value={customOpenAIModelName}
                          onChange={(e) => setCustomOpenAIModelName(e.target.value)}
                          placeholder="e.g., glm-4.5"
                          className="border-gray-200"
                        />
                      </div>
                    </>
                  )}

                  {/* OpenAI Fields */}
                  {chatModelProvider === 'openai' && (
                    <>
                      <div>
                        <label htmlFor="openai-api-key" className="block text-sm font-medium text-gray-700 mb-2">
                          OpenAI API Key <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="openai-api-key"
                          type="password"
                          value={openAIApiKey}
                          onChange={(e) => setOpenAIApiKey(e.target.value)}
                          placeholder="sk-..."
                          className="border-gray-200 font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">OpenAI Platform</a>
                        </p>
                      </div>

                      <div>
                        <label htmlFor="openai-model-name" className="block text-sm font-medium text-gray-700 mb-2">
                          Model Name <span className="text-red-500">*</span>
                        </label>
                        <Select value={openAIModelName} onValueChange={setOpenAIModelName}>
                          <SelectTrigger id="openai-model-name" className="border-gray-200">
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gpt-5">GPT-5</SelectItem>
                            <SelectItem value="gpt-5-mini">GPT-5 Mini</SelectItem>
                            <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                            <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini</SelectItem>
                            <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                            <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                            <SelectItem value="gpt-4o-audio-preview">GPT-4o Audio Preview</SelectItem>
                            <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                            <SelectItem value="gpt-4-turbo-preview">GPT-4 Turbo Preview</SelectItem>
                            <SelectItem value="gpt-4">GPT-4</SelectItem>
                            <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
                            <SelectItem value="gpt-3.5-turbo-16k">GPT-3.5 Turbo 16K</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Anthropic Fields */}
                  {chatModelProvider === 'anthropic' && (
                    <>
                      <div>
                        <label htmlFor="anthropic-api-key" className="block text-sm font-medium text-gray-700 mb-2">
                          Anthropic API Key <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="anthropic-api-key"
                          type="password"
                          value={anthropicApiKey}
                          onChange={(e) => setAnthropicApiKey(e.target.value)}
                          placeholder="sk-ant-..."
                          className="border-gray-200 font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Get your API key from <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Anthropic Console</a>
                        </p>
                      </div>

                      <div>
                        <label
                          htmlFor="anthropic-model-name"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Model Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="anthropic-model-name"
                          value={anthropicModelName}
                          onChange={(e) => setAnthropicModelName(e.target.value)}
                          placeholder="Ví dụ: claude-3-haiku-20240307 hoặc model 4.x mới từ Anthropic"
                          className="border-gray-200 font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Dán đúng model ID từ Anthropic Console (ví dụ các model 4.x mới trong bảng
                          rate limit).
                        </p>
                      </div>
                    </>
                  )}

                  {/* Google Fields */}
                  {chatModelProvider === 'google' && (
                    <>
                      <div>
                        <label htmlFor="google-api-key" className="block text-sm font-medium text-gray-700 mb-2">
                          Google API Key <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="google-api-key"
                          type="password"
                          value={googleApiKey}
                          onChange={(e) => setGoogleApiKey(e.target.value)}
                          placeholder="Enter your API key"
                          className="border-gray-200 font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Get your API key from <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google AI Studio</a>
                        </p>
                      </div>

                      <div>
                        <label htmlFor="google-project-id" className="block text-sm font-medium text-gray-700 mb-2">
                          Project ID
                        </label>
                        <Input
                          id="google-project-id"
                          value={googleProjectId}
                          onChange={(e) => setGoogleProjectId(e.target.value)}
                          placeholder="your-project-id"
                          className="border-gray-200"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Optional: Your Google Cloud project ID
                        </p>
                      </div>

                      <div>
                        <label htmlFor="google-model-name" className="block text-sm font-medium text-gray-700 mb-2">
                          Model Name <span className="text-red-500">*</span>
                        </label>
                        <Select value={googleModelName} onValueChange={setGoogleModelName}>
                          <SelectTrigger id="google-model-name" className="border-gray-200">
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gemini-pro">Gemini Pro</SelectItem>
                            <SelectItem value="gemini-pro-vision">Gemini Pro Vision</SelectItem>
                            <SelectItem value="gemini-ultra">Gemini Ultra</SelectItem>
                            <SelectItem value="text-bison">Text Bison</SelectItem>
                            <SelectItem value="chat-bison">Chat Bison</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Cohere Fields */}
                  {chatModelProvider === 'cohere' && (
                    <>
                      <div>
                        <label htmlFor="cohere-api-key" className="block text-sm font-medium text-gray-700 mb-2">
                          Cohere API Key <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="cohere-api-key"
                          type="password"
                          value={cohereApiKey}
                          onChange={(e) => setCohereApiKey(e.target.value)}
                          placeholder="Enter your API key"
                          className="border-gray-200 font-mono text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Get your API key from <a href="https://dashboard.cohere.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Cohere Dashboard</a>
                        </p>
                      </div>

                      <div>
                        <label htmlFor="cohere-model-name" className="block text-sm font-medium text-gray-700 mb-2">
                          Model Name <span className="text-red-500">*</span>
                        </label>
                        <Select value={cohereModelName} onValueChange={setCohereModelName}>
                          <SelectTrigger id="cohere-model-name" className="border-gray-200">
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="command">Command</SelectItem>
                            <SelectItem value="command-light">Command Light</SelectItem>
                            <SelectItem value="command-nightly">Command Nightly</SelectItem>
                            <SelectItem value="command-light-nightly">Command Light Nightly</SelectItem>
                            <SelectItem value="command-r">Command R</SelectItem>
                            <SelectItem value="command-r-plus">Command R Plus</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Embedding Model Settings */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Embedding Model Settings</h3>

                <div className="grid gap-6">
                  <div>
                    <label htmlFor="embedding-model-provider" className="block text-sm font-medium text-gray-700 mb-2">
                      Embedding Model Provider
                    </label>
                    <Select value={embeddingModelProvider} onValueChange={setEmbeddingModelProvider}>
                      <SelectTrigger id="embedding-model-provider" className="border-gray-200">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hugging-face">Hugging Face</SelectItem>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="cohere">Cohere</SelectItem>
                        <SelectItem value="sentence-transformers">Sentence Transformers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="embedding-model" className="block text-sm font-medium text-gray-700 mb-2">
                      Embedding Model
                    </label>
                    <Select value={embeddingModel} onValueChange={setEmbeddingModel}>
                      <SelectTrigger id="embedding-model" className="border-gray-200">
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bge-small">BGE Small</SelectItem>
                        <SelectItem value="bge-base">BGE Base</SelectItem>
                        <SelectItem value="bge-large">BGE Large</SelectItem>
                        <SelectItem value="text-embedding-ada-002">OpenAI Ada 002</SelectItem>
                        <SelectItem value="text-embedding-3-small">OpenAI 3 Small</SelectItem>
                        <SelectItem value="text-embedding-3-large">OpenAI 3 Large</SelectItem>
                        <SelectItem value="embed-english-v3.0">Cohere English v3.0</SelectItem>
                        <SelectItem value="embed-multilingual-v3.0">Cohere Multilingual v3.0</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

